/**
 * Orders API utilities
 * Handles all order-related API calls using authFetch
 */
import { authFetch } from './auth';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Orders API
 */
export const ordersAPI = {
  /**
   * Get all orders for the current user
   */
  async list() {
    const response = await authFetch(`${API_BASE}/api/orders/`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch orders');
    }
    return response.json();
  },

  /**
   * Create a new order
   */
  async create(orderData) {
    const response = await authFetch(`${API_BASE}/api/orders/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to create order');
    }
    return response.json();
  },

  /**
   * Get a specific order by ID
   */
  async get(orderId) {
    const response = await authFetch(`${API_BASE}/api/orders/${orderId}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch order');
    }
    return response.json();
  },

  /**
   * Update an order
   */
  async update(orderId, updates) {
    const response = await authFetch(`${API_BASE}/api/orders/${orderId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to update order');
    }
    return response.json();
  },
};

/**
 * Files API
 */
export const filesAPI = {
  /**
   * Request a presigned upload URL
   */
  async getUploadUrl(orderId, kind, filename, contentType, sizeBytes) {
    const response = await authFetch(`${API_BASE}/api/files/presigned-upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order_id: orderId,
        kind,
        filename,
        content_type: contentType,
        size_bytes: sizeBytes,
      }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to get upload URL');
    }
    return response.json();
  },

  /**
   * Confirm file upload completed
   */
  async confirmUpload(fileId) {
    const response = await authFetch(`${API_BASE}/api/files/confirm-upload/${fileId}`, {
      method: 'POST',
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to confirm upload');
    }
    return response.json();
  },

  /**
   * List files for an order
   */
  async listForOrder(orderId) {
    const response = await authFetch(`${API_BASE}/api/files/order/${orderId}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch files');
    }
    return response.json();
  },

  /**
   * Get presigned download URL
   */
  async getDownloadUrl(orderId, fileId) {
    const response = await authFetch(`${API_BASE}/api/files/presigned-download/${orderId}/${fileId}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to get download URL');
    }
    return response.json();
  },

  /**
   * Delete a file
   */
  async delete(fileId) {
    const response = await authFetch(`${API_BASE}/api/files/${fileId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to delete file');
    }
    return response.json();
  },

  /**
   * Upload file to S3 using presigned URL
   */
  async uploadToS3(uploadUrl, file, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });
      
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  },
};

/**
 * Revisions API (Customer-facing)
 */
export const revisionsAPI = {
  /**
   * List all revisions for an order
   */
  async listForOrder(orderId) {
    const response = await authFetch(`${API_BASE}/api/revisions/order/${orderId}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch revisions');
    }
    return response.json();
  },

  /**
   * Approve a revision
   */
  async approve(revisionId) {
    const response = await authFetch(`${API_BASE}/api/revisions/${revisionId}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to approve revision');
    }
    return response.json();
  },

  /**
   * Request changes on a revision
   */
  async requestChanges(revisionId, feedback) {
    const response = await authFetch(`${API_BASE}/api/revisions/${revisionId}/request-changes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ feedback }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to request changes');
    }
    return response.json();
  },
};

/**
 * Payments API
 */
export const paymentsAPI = {
  /**
   * Create a Stripe Checkout session for an order
   */
  async createCheckoutSession(orderId) {
    const originUrl = window.location.origin;
    const response = await authFetch(`${API_BASE}/api/payments/checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        order_id: orderId,
        origin_url: originUrl 
      }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to create checkout session');
    }
    return response.json();
  },

  /**
   * Get payment status by session ID (for polling after checkout)
   */
  async getStatusBySession(sessionId) {
    const response = await authFetch(`${API_BASE}/api/payments/status/${sessionId}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to get payment status');
    }
    return response.json();
  },

  /**
   * Get payment status for an order
   */
  async getOrderPaymentStatus(orderId) {
    const response = await authFetch(`${API_BASE}/api/payments/order/${orderId}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to get payment status');
    }
    return response.json();
  },
};

export default { ordersAPI, filesAPI, paymentsAPI };
